import type { Recommendation } from '@/types';

import { SubscribeButton } from './SubscribeButton';

interface RecommendationRowProps {
  email?: string;
  recommendation: Recommendation;
  submittedIds: string[];
  translations?: Record<string, string>;
  onSubmitSuccess?: (recommendations: Recommendation[]) => void;
  SubscribeForm: React.ComponentType<{
    recommendations: Recommendation[];
    onSubmitSuccess?: (recommendations: Recommendation[]) => void;
    children: (props: { isLoading: boolean; email?: string }) => React.ReactNode;
  }>;
}

export const RecommendationRow: React.FC<RecommendationRowProps> = ({
  recommendation,
  submittedIds,
  translations,
  onSubmitSuccess,
  SubscribeForm,
}) => {
  return (
    <div
      key={recommendation.id}
      className="p-4 rounded-[--site-theme-border-radius] border border-[--site-theme-border-color] bg-[--site-theme-background-color] flex flex-col gap-2"
    >
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-start">
          <img
            src={recommendation.logo_url}
            alt={`${recommendation.name}-logo`}
            className="w-[56px] h-[56px] rounded grow-0"
          />
          <SubscribeForm recommendations={[recommendation]} onSubmitSuccess={() => onSubmitSuccess?.([recommendation])}>
            {({ isLoading, email }) => (
              <SubscribeButton
                recommendation={recommendation}
                isLoading={isLoading}
                email={email}
                submittedIds={submittedIds}
                translations={translations}
              />
            )}
          </SubscribeForm>
        </div>

        <div className="flex flex-col gap-1" style={{ fontFamily: 'var(--site-theme-header-font)' }}>
          {recommendation.sponsored && (
            <p className="text-xs font-medium text-[--site-theme-text-on-background-color] opacity-60">
              {translations?.['recommendations.modal.sponsored'] || 'Sponsored'}
            </p>
          )}
          <h3 className="text-lg font-semibold text-[--site-theme-text-on-background-color] leading-snug line-clamp-2">
            {recommendation.name}
          </h3>
        </div>
      </div>

      {!!recommendation.badge && (
        <span className="shadow-xs inline-flex items-center text-xs font-medium relative group rounded bg-indigo-100 text-indigo-900 border-indigo-200 border px-1.5 py-0.5 gap-x-1 w-max">
          <span className="w-full block overflow-hidden whitespace-nowrap text-ellipsis">{recommendation.badge}</span>
        </span>
      )}
      <p
        className="text-base font-normal text-[--site-theme-text-on-background-color] opacity-60 line-clamp-2"
        style={{ fontFamily: 'var(--site-theme-body-font)' }}
      >
        {recommendation.description}
      </p>
    </div>
  );
};
